/* Funduino Anleitung — das gesamte Verhalten der Blogbeitrags-Vorlage.
 *
 * Eine Datei, kein Framework, keine Abhaengigkeit. Verwendet werden nur
 * IntersectionObserver, localStorage, Blob und classList — alles seit Jahren
 * Standard.
 *
 * Aufbau: eine IIFE, darin je eine init-Funktion pro Baustein. Jede beginnt
 * mit einem Waechter und tut nichts, wenn ihr Markup fehlt. Dadurch ist die
 * Datei auf jeder Seite unschaedlich, auch wenn nur ein Teil der Bloecke
 * gepflegt ist.
 *
 * Grundsatz an allen Stellen: KEINE Beschriftung steht hier im Code. Alle
 * Texte kommen aus data-Attributen, die das Liquid aus den Section-Settings
 * fuellt. Sonst waeren sie nicht mehr redaktionell aenderbar.
 *
 * Eine einzige Abhaengigkeit, und die kommt aus dem Theme selbst:
 * Horizon scrollt ab 990px NICHT das Fenster. html und body stehen dort auf
 * overflow:hidden und Fensterhoehe; gescrollt wird .page-wrapper. window.scrollTo
 * und window.scrollY sind damit wirkungslos, und ein IntersectionObserver ohne
 * ausdruecklichen root loest nie aus, weil er den falschen Scrollbereich
 * beobachtet. Beides waere hier lautlos kaputt gewesen — die Sprungleiste haette
 * sich nicht bewegt und der aktive Schritt nie gewechselt. Das Theme loest das
 * in assets/scroll-container.js und stellt die Helfer ueber die Importmap
 * bereit; deshalb ist diese Datei ein Modul.
 */
import { scrollTo as seiteScrollen, getScrollTop, getScrollContainer, getIntersectionRoot } from '@theme/scroll-container';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

(function () {
  'use strict';

  /* Frueher stand hier ein fruehes return, wenn es keine Anleitungs-Wurzel
     gab. Das machte die Datei blind fuer shopify:section:load: Im
     Theme-Editor rendert Shopify eine Section neu, und danach war niemand
     mehr da, der sie wieder einrichtet — Video, Board-Auswahl, Bewertung und
     Kommentare blieben tot, bis die Vorschau komplett neu geladen wurde.

     Die drei Geschwisterdateien machen es richtig: assets/fu-anl.js:210,431,569,
     assets/fu-header-drawer.js:210 und assets/fu-mega-menu.js:405 haengen sich
     zusaetzlich an shopify:section:load.

     wurzel wird jetzt in start() gesetzt statt hier. Der Riegel sitzt am
     Element (data-fu-blog-ready), nicht am Modul — so laeuft start() beliebig
     oft, richtet aber jede Wurzel nur einmal ein. */
  var wurzel = null;

  var sanft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function alle(sel, kontext) {
    return Array.prototype.slice.call((kontext || wurzel).querySelectorAll(sel));
  }

  function scrollZu(y) {
    seiteScrollen({ top: y, behavior: sanft ? 'smooth' : 'auto' });
  }

  /* ------------------------------------------------------------------
     Bildergalerien in den Schritten
     ------------------------------------------------------------------ */
  function initGalerien() {
    alle('[data-fu-gallery]').forEach(function (gal) {
      var track = gal.querySelector('[data-fu-gallery-track]');
      if (!track) return;

      /* Die Anzahl kommt aus dem DOM, nicht aus einer Zahl im Skript. Damit
         funktioniert dieselbe Datei fuer zwei wie fuer zwoelf Bilder. */
      var anzahl = track.children.length;
      if (anzahl < 2) return;

      var stand = gal.querySelector('[data-fu-gal-count]');
      var zurueck = gal.querySelector('[data-fu-gal-prev]');
      var vor = gal.querySelector('[data-fu-gal-next]');
      /* Vorschau-Knoepfe gibt es nur in der Kopfgalerie; in den Schritten
         ist die Liste leer, und die beiden Schleifen unten tun nichts. */
      var vorschau = alle('[data-fu-gal-thumb]', gal);
      var index = 0;

      function zeichne() {
        track.style.transform = 'translateX(-' + index * 100 + '%)';
        if (stand) stand.textContent = index + 1 + ' / ' + anzahl;
        vorschau.forEach(function (knopf) {
          knopf.setAttribute('data-aktiv', knopf.getAttribute('data-fu-gal-thumb') === String(index) ? 'true' : 'false');
        });
        Array.prototype.forEach.call(track.children, function (folie, i) {
          /* Nicht sichtbare Folien aus der Tabulatorreihenfolge nehmen —
             sonst wandert der Fokus in etwas, das niemand sieht. */
          folie.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          alle('a, button', folie).forEach(function (el) {
            if (i === index) el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', '-1');
          });
        });
      }

      function schiebe(d) {
        index = (index + d + anzahl) % anzahl;
        zeichne();
      }

      if (zurueck) zurueck.addEventListener('click', function () { schiebe(-1); });
      if (vor) vor.addEventListener('click', function () { schiebe(1); });

      /* Klick auf ein Vorschaubild springt direkt zur Folie. Der Index steht
         im Markup (0-basiert, im Takt der Schiene); ein unbrauchbarer Wert
         wird ignoriert statt auf Folie 0 zu fallen. */
      vorschau.forEach(function (knopf) {
        knopf.addEventListener('click', function () {
          var ziel = parseInt(knopf.getAttribute('data-fu-gal-thumb'), 10);
          if (isNaN(ziel) || ziel < 0 || ziel >= anzahl) return;
          index = ziel;
          zeichne();
        });
      });

      /* Wischen. Erst ab 40 Pixel, damit ein Antippen nicht schon schaltet. */
      var startX = null;
      track.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 40) schiebe(dx < 0 ? 1 : -1);
      }, { passive: true });

      zeichne();
    });
  }

  /* ------------------------------------------------------------------
     Fortschritt: erledigte Schritte
     ------------------------------------------------------------------ */
  function initFortschritt() {
    var knoepfe = alle('[data-fu-done]');
    if (!knoepfe.length) return;

    /* Der Schluessel haengt am Beitrags-Handle. Ein fester Schluessel wuerde
       den Fortschritt aller Anleitungen zusammenwerfen. */
    var schluessel = 'fu-anleitung-' + (wurzel.getAttribute('data-fu-blog-handle') || 'unbenannt');
    var stand = {};
    try {
      stand = JSON.parse(window.localStorage.getItem(schluessel)) || {};
    } catch (e) {
      stand = {};
    }

    /* Die Zaehlerzeile ueber der Strecke. Vorlage und Gesamtzahl kommen aus
       dem Markup; hier wird nur gezaehlt und eingesetzt. */
    var status = wurzel.querySelector('[data-fu-status]');

    function zeichne() {
      var erledigt = 0;
      knoepfe.forEach(function (knopf) {
        var n = knopf.getAttribute('data-fu-done');
        var ist = !!stand[n];
        if (ist) erledigt += 1;
        knopf.setAttribute('data-erledigt', ist ? 'true' : 'false');
        knopf.setAttribute('aria-pressed', ist ? 'true' : 'false');
        var text = knopf.querySelector('[data-fu-done-text]');
        if (text) {
          text.textContent = ist
            ? knopf.getAttribute('data-label-erledigt') || text.textContent
            : knopf.getAttribute('data-label-offen') || text.textContent;
        }
        var marke = wurzel.querySelector('[data-fu-navmark="' + n + '"]');
        if (marke) marke.setAttribute('data-erledigt', ist ? 'true' : 'false');
        /* Der Schritt selbst bekommt den Stand fuer die abgesetzte
           Darstellung (grauer Grund, zurueckgenommener Inhalt). */
        var schritt = wurzel.querySelector('[data-fu-step="' + n + '"]');
        if (schritt) schritt.setAttribute('data-erledigt', ist ? 'true' : 'false');
      });
      if (status) {
        var vorlage = status.getAttribute('data-vorlage') || '';
        var gesamt = status.getAttribute('data-gesamt') || String(knoepfe.length);
        var ziel = status.querySelector('[data-fu-status-text]') || status;
        ziel.textContent = vorlage.replace('{n}', erledigt).replace('{gesamt}', gesamt);
        status.hidden = false;
      }
    }

    knoepfe.forEach(function (knopf) {
      knopf.addEventListener('click', function () {
        var n = knopf.getAttribute('data-fu-done');
        stand[n] = !stand[n];
        try {
          window.localStorage.setItem(schluessel, JSON.stringify(stand));
        } catch (e) {
          /* Privater Modus oder volles Kontingent: die Anzeige stimmt
             trotzdem, nur ueberlebt sie den Seitenwechsel nicht. */
        }
        zeichne();
      });
    });

    zeichne();
  }

  /* ------------------------------------------------------------------
     Aktiver Schritt: speist Sprungleiste und Seitenleiste
     ------------------------------------------------------------------ */
  function initSchrittverfolgung() {
    var schritte = alle('[data-fu-step]');
    if (!schritte.length) return;

    var leiste = wurzel.querySelector('[data-fu-jump]');
    var stand = wurzel.querySelector('[data-fu-jump-stand]');
    var aktiv = 1;

    function setzeAktiv(n) {
      aktiv = n;
      if (stand) {
        var muster = leiste ? leiste.getAttribute('data-vorlage') || '' : '';
        stand.textContent = muster.replace('{n}', n).replace('{gesamt}', schritte.length);
      }
      alle('[data-fu-navstep]').forEach(function (link) {
        link.setAttribute('data-aktiv', link.getAttribute('data-fu-navstep') === String(n) ? 'true' : 'false');
      });
      if (leiste) {
        var prev = leiste.querySelector('[data-fu-jump-prev]');
        var next = leiste.querySelector('[data-fu-jump-next]');
        if (prev) prev.disabled = n <= 1;
        if (next) next.disabled = n >= schritte.length;
      }
    }

    /* Das Beobachtungsband ist absichtlich schmal und liegt im oberen
       Drittel: dort steht der Schritt, den man gerade liest, nicht der, der
       unten gerade hereinkommt. */
    if ('IntersectionObserver' in window) {
      /* root muss ab 990px ausdruecklich .page-wrapper sein, sonst beobachtet
         der Browser das Fenster — und das scrollt in diesem Theme gar nicht. */
      var wurzelbereich = getIntersectionRoot();

      var beob = new IntersectionObserver(
        function (eintraege) {
          eintraege.forEach(function (e) {
            if (e.isIntersecting) setzeAktiv(Number(e.target.getAttribute('data-fu-step')));
          });
        },
        { root: wurzelbereich, rootMargin: '-35% 0px -55% 0px' }
      );
      schritte.forEach(function (s) { beob.observe(s); });

      /* Die Leiste erscheint erst, wenn der erste Schritt erreicht ist —
         davor hat sie nichts anzuzeigen.
         Das schmale Band am oberen Rand ist noetig, weil ein Schritt hoeher
         sein kann als der Bildschirm: Ohne Band faellt zwischen Eintritt von
         unten und vollstaendigem Hinauslaufen kein Ereignis, und die Leiste
         fehlte den ganzen ersten Schritt. Jeder Zustandswechsel braucht eine
         Schwellen-Ueberquerung; -99% statt -100%, weil ein Band der Hoehe 0
         isIntersecting unzuverlaessig macht. Ist der Schritt oben aus dem
         Band gelaufen, haelt top < 0 die Leiste sichtbar. */
      if (leiste) {
        var wache = new IntersectionObserver(function (eintraege) {
          eintraege.forEach(function (e) {
            leiste.setAttribute('data-sichtbar', e.isIntersecting || e.boundingClientRect.top < 0 ? 'true' : 'false');
          });
        }, { root: wurzelbereich, rootMargin: '0px 0px -99% 0px' });
        wache.observe(schritte[0]);
      }
    } else if (leiste) {
      leiste.setAttribute('data-sichtbar', 'true');
    }

    function springe(n) {
      var ziel = schritte[n - 1];
      if (!ziel) return;
      /* getScrollTop statt window.scrollY: Der Bezugspunkt ist der
         Scrollbereich des Themes, nicht das Fenster.
         Das Polster kommt aus dem scroll-padding-top desselben Bereichs
         (assets/fu-custom.css) — scrollTo() beachtet es im Gegensatz zu
         #-Links nicht von selbst. Ohne das Polster schoebe der Pfeilknopf
         die Ueberschrift hinter den klebenden Kopf, waehrend der
         Seitenleisten-Link denselben Schritt richtig setzt. So landen beide
         auf demselben Punkt. */
      var bereich = getScrollContainer();
      var polster = parseFloat(getComputedStyle(bereich).scrollPaddingTop) || 16;
      scrollZu(ziel.getBoundingClientRect().top + getScrollTop() - polster);
      setzeAktiv(n);
    }

    if (leiste) {
      var top = leiste.querySelector('[data-fu-jump-top]');
      var ende = leiste.querySelector('[data-fu-jump-end]');
      var prev = leiste.querySelector('[data-fu-jump-prev]');
      var next = leiste.querySelector('[data-fu-jump-next]');
      if (top) top.addEventListener('click', function () { scrollZu(0); });
      if (ende) ende.addEventListener('click', function () { scrollZu(getScrollContainer().scrollHeight); });
      if (prev) prev.addEventListener('click', function () { springe(Math.max(1, aktiv - 1)); });
      if (next) next.addEventListener('click', function () { springe(Math.min(schritte.length, aktiv + 1)); });
    }

    setzeAktiv(1);
  }

  /* ------------------------------------------------------------------
     Halt der Schrittliste am Seitenende

     Ab dem Abschluss laufen Abschluss, passende Anleitungen und Kommentare
     ueber die volle Breite. Die Schrittliste klebt bis dahin in der
     Seitenleiste; bliebe sie kleben, staende sie ueber dem seitenbreiten
     Inhalt. Sie haelt deshalb an, sobald der Abschluss von unten in den
     Blick kommt, und klebt beim Hochscrollen wieder — sie verschwindet
     nie, sie friert nur ein.

     Warum Skript und nicht CSS: Ein klebendes Element endet dort, wo sein
     Elternblock endet. Die Seitenleiste ist per align-self:stretch so hoch
     wie die ganze Spalte, damit die Liste den Weg mitgeht — sie kann also
     nicht auch noch frueher aufhoeren. Die Grenze haengt an der
     Scrollposition, und die kennt CSS nicht.

     Der Beobachter meldet, ob der Abschluss ueberhaupt im Blickfeld ist.
     Das genuegt: Er ist der erste seitenbreite Baustein, alles Weitere
     liegt darunter.
     ------------------------------------------------------------------ */
  function initListeHalt() {
    var abschluss = wurzel.querySelector('.fu-blog__abschluss');
    /* Ohne Abschlusstext gibt es nichts, woran die Liste anhalten muesste.
       Dann darf sie den ganzen Weg kleben — die naechste Section beginnt
       direkt darunter und liegt tiefer als der Klebebereich. */
    if (!abschluss) {
      wurzel.setAttribute('data-fu-sb-frei', 'false');
      return;
    }
    /* Ohne IntersectionObserver bleibt die sichere Vorgabe aus dem Markup
       stehen: nichts klebt. Lieber eine Liste, die mitscrollt, als eine,
       die ueber dem Text liegt. */
    if (!('IntersectionObserver' in window)) return;

    var liste = wurzel.querySelector('.fu-blog__sb-schritte');

    /* Wie weit der Blick von unten beschnitten wird, damit der Beobachter
       genau dann meldet, wenn der Abschluss die UNTERKANTE DER LISTE
       erreicht — nicht erst, wenn er den halben Bildschirm fuellt.

       Hier stand bis zum 21.09.2026 fest -55%. Das war eine Schaetzung fuer
       eine Liste, die bei rund 191px klebt und rund 340px hoch ist: 531 von
       900px, also 59% — grob passend, aber eben geraten. Sobald der Kopf des
       Anleitungsbereichs dauerhaft klebte, stimmte die Zahl nicht mehr, und
       die weisse Liste legte sich gemessen 123px (1440x900) bzw. 132px
       (1024x768) ueber Ueberschrift und Schlusstext des Abschlusses. Auf
       sechs der zwoelf Anleitungen mit Schlusstext.

       Beide Groessen haengen am Fenster: Der Klebeabstand kommt aus
       --fu-klebe-oben, die Hoehe der Liste aus max-height. Deshalb wird
       gerechnet und nicht geschaetzt — und bei jeder Groessenaenderung neu.

       getComputedStyle(liste).top liefert den aufgeloesten Klebewert in
       Pixeln (191px, 241px). Unter 900px steht dort 'auto': Dort klebt gar
       nichts, die Frage stellt sich nicht, und der alte Schaetzwert bleibt
       als Rueckfall stehen. */
    function randUnten() {
      var bezug = getIntersectionRoot();
      var fensterhoehe = bezug ? bezug.clientHeight : window.innerHeight;
      if (!liste) return Math.round(fensterhoehe * 0.55);
      var klebeOben = parseFloat(window.getComputedStyle(liste).top);
      if (isNaN(klebeOben)) return Math.round(fensterhoehe * 0.55);
      var unterkante = klebeOben + liste.getBoundingClientRect().height;
      return Math.max(0, Math.round(fensterhoehe - unterkante));
    }

    var beob = null;

    function beobachten() {
      if (beob) beob.disconnect();
      beob = new IntersectionObserver(
        function (eintraege) {
          eintraege.forEach(function (e) {
            wurzel.setAttribute('data-fu-sb-frei', e.isIntersecting ? 'true' : 'false');
          });
        },
        { root: getIntersectionRoot(), rootMargin: '0px 0px -' + randUnten() + 'px 0px' }
      );
      beob.observe(abschluss);
    }

    beobachten();

    /* Neu aufsetzen statt nachjustieren: rootMargin laesst sich an einem
       bestehenden Beobachter nicht aendern. Der Zeitgeber fasst die vielen
       Ereignisse beim Ziehen am Fensterrand zu einem zusammen. */
    var zeitgeber = null;
    window.addEventListener('resize', function () {
      if (zeitgeber) clearTimeout(zeitgeber);
      zeitgeber = setTimeout(beobachten, 150);
    });
  }

  /* ------------------------------------------------------------------
     Einleitung auf- und zuklappen
     ------------------------------------------------------------------ */
  function initMehrLesen() {
    alle('[data-fu-more]').forEach(function (knopf) {
      var ziel = wurzel.querySelector('#' + knopf.getAttribute('aria-controls'));
      if (!ziel) return;

      /* Der Text startet AUFGEKLAPPT. Die Einleitung ist der Absatz, der
         entscheidet, ob jemand weiterliest — abgeschnitten kostet er genau
         die Leser, die er gewinnen soll. Der Knopf bleibt trotzdem da und
         klappt zu, wer die Seite ueberfliegen will.

         Zusammengeklappt wird ueber data-offen und CSS, nie ueber hidden:
         Der vollstaendige Text muss im Textfluss stehen bleiben, damit
         Suchmaschinen und Screenreader ihn finden. */
      knopf.hidden = false;
      ziel.setAttribute('data-offen', 'true');
      knopf.setAttribute('aria-expanded', 'true');
      var starttext = knopf.querySelector('[data-fu-more-text]');
      if (starttext) {
        starttext.textContent = knopf.getAttribute('data-label-weniger') || starttext.textContent;
      }
      knopf.addEventListener('click', function () {
        var offen = knopf.getAttribute('aria-expanded') === 'true';
        knopf.setAttribute('aria-expanded', offen ? 'false' : 'true');
        ziel.setAttribute('data-offen', offen ? 'false' : 'true');
        var text = knopf.querySelector('[data-fu-more-text]');
        if (text) {
          text.textContent = offen
            ? knopf.getAttribute('data-label-mehr') || text.textContent
            : knopf.getAttribute('data-label-weniger') || text.textContent;
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Teilen
     ------------------------------------------------------------------ */
  function initTeilen() {
    alle('[data-fu-share]').forEach(function (knopf) {
      var meldung = knopf.parentNode.querySelector('[data-fu-share-status]');
      var uhr = null;

      function melde(text) {
        if (!meldung) return;
        meldung.textContent = text;
        meldung.hidden = false;
        window.clearTimeout(uhr);
        uhr = window.setTimeout(function () { meldung.hidden = true; }, 2400);
      }

      knopf.addEventListener('click', function () {
        var url = window.location.href;
        if (navigator.share) {
          navigator
            .share({ title: document.title, url: url })
            .then(function () { melde(knopf.getAttribute('data-label-geteilt') || ''); })
            /* Ein Abbruch ist kein Erfolg — der Entwurf meldet auch dann
               "Link kopiert", was schlicht falsch ist. */
            .catch(function () {});
          return;
        }
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(url).then(
            function () { melde(knopf.getAttribute('data-label-kopiert') || ''); },
            function () { melde(knopf.getAttribute('data-label-fehler') || ''); }
          );
          return;
        }
        melde(kopiereUeberFeld(url) ? knopf.getAttribute('data-label-kopiert') || '' : knopf.getAttribute('data-label-fehler') || '');
      });
    });
  }

  /* Rueckfall fuer Umgebungen ohne Zwischenablage-API — vor allem der lokale
     Vorschau-Server, der ueber http laeuft. */
  function kopiereUeberFeld(text) {
    var feld = document.createElement('textarea');
    feld.value = text;
    feld.setAttribute('readonly', '');
    feld.style.cssText = 'position:absolute;left:-9999px;top:0';
    document.body.appendChild(feld);
    feld.select();
    var gut = false;
    try {
      gut = document.execCommand('copy');
    } catch (e) {
      gut = false;
    }
    feld.remove();
    return gut;
  }

  /* ------------------------------------------------------------------
     Code-Bloecke
     ------------------------------------------------------------------ */
  var SCHLUESSELWOERTER = {
    void: 1, int: 1, float: 1, long: 1, char: 1, bool: 1, struct: 1, const: 1,
    return: 1, if: 1, else: 1, for: 1, while: 1, switch: 1, case: 1, break: 1,
    true: 1, false: 1, HIGH: 1, LOW: 1, OUTPUT: 1, INPUT: 1, INPUT_PULLUP: 1
  };

  /* Gemeinsame Einrueckung abschneiden. Nur links, ohne neu einzurruecken —
     relative Verschachtelung bleibt damit erhalten. */
  function entruecke(quelle) {
    var zeilen = String(quelle).replace(/\t/g, '  ').replace(/^\n+/, '').replace(/\s+$/, '').split('\n');
    var min = Infinity;
    zeilen.forEach(function (z) {
      if (!z.trim()) return;
      var n = z.match(/^ */)[0].length;
      if (n < min) min = n;
    });
    if (!isFinite(min)) min = 0;
    return zeilen
      .map(function (z) { return z.slice(min); })
      .join('\n');
  }

  function spanne(klasse, text) {
    var s = document.createElement('span');
    if (klasse) s.className = 'fu-blog__code-' + klasse;
    s.textContent = text;
    return s;
  }

  /* Ein einfacher Arduino-Faerber, bewusst mit denselben Grenzen wie die
     Vorlage: keine Blockkommentare, keine einfachen Anfuehrungszeichen. Fuer
     die vorhandenen Sketches reicht das, und was er nicht erkennt, bleibt
     einfach unauffaellig. */
  function faerbeZeile(zeile) {
    var div = document.createElement('div');
    div.className = 'fu-blog__code-zeile';
    var rest = zeile;
    var kommentar = null;
    var ci = rest.indexOf('//');
    if (ci > -1) {
      kommentar = rest.slice(ci);
      rest = rest.slice(0, ci);
    }

    if (/^\s*#/.test(rest)) {
      div.appendChild(spanne('pre', rest));
    } else {
      var re = /("(?:[^"\\]|\\.)*")|(\b\d+\b)|([A-Za-z_][A-Za-z0-9_]*)|(\s+)|([^\s])/g;
      var m;
      while ((m = re.exec(rest)) !== null) {
        if (m[1]) div.appendChild(spanne('str', m[1]));
        else if (m[2]) div.appendChild(spanne('num', m[2]));
        else if (m[3]) {
          var danach = rest.slice(m.index + m[3].length);
          if (Object.prototype.hasOwnProperty.call(SCHLUESSELWOERTER, m[3])) div.appendChild(spanne('key', m[3]));
          else if (/^\s*\(/.test(danach)) div.appendChild(spanne('fn', m[3]));
          else div.appendChild(spanne('', m[3]));
        } else div.appendChild(spanne('', m[0]));
      }
    }
    if (kommentar) div.appendChild(spanne('com', kommentar));
    if (!div.childNodes.length) div.appendChild(document.createTextNode(''));
    return div;
  }

  function dateiname(bezeichnung) {
    return (
      String(bezeichnung || 'sketch')
        .toLowerCase()
        .replace(/[äöüß]/g, function (c) {
          return { 'ä': 'ae', 'ö': 'oe', 'ü': 'ue', 'ß': 'ss' }[c];
        })
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'sketch'
    );
  }

  function initCode() {
    alle('[data-fu-code]').forEach(function (block) {
      var pre = block.querySelector('pre');
      if (!pre) return;

      /* Der rohe Text wird VOR dem Faerben festgehalten. Danach steht im
         Element nur noch Markup, aus dem sich Einrueckung und Zeilenumbrueche
         nicht zuverlaessig zurueckgewinnen lassen. */
      var roh = entruecke(pre.textContent);
      var bezeichnung = block.getAttribute('data-fu-code-label') || '';

      var neu = document.createDocumentFragment();
      roh.split('\n').forEach(function (z) { neu.appendChild(faerbeZeile(z)); });
      pre.textContent = '';
      pre.appendChild(neu);

      var kopieren = block.querySelector('[data-fu-code-copy]');
      if (kopieren) {
        var uhr = null;
        kopieren.addEventListener('click', function () {
          function fertig(gut) {
            var text = kopieren.querySelector('[data-fu-code-copy-text]');
            if (!text) return;
            var vorher = kopieren.getAttribute('data-label-kopieren') || text.textContent;
            text.textContent = gut
              ? kopieren.getAttribute('data-label-kopiert') || vorher
              : kopieren.getAttribute('data-label-fehler') || vorher;
            kopieren.setAttribute('data-zustand', gut ? 'gut' : 'schlecht');
            window.clearTimeout(uhr);
            uhr = window.setTimeout(function () {
              text.textContent = vorher;
              kopieren.removeAttribute('data-zustand');
            }, 1800);
          }
          if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(roh).then(function () { fertig(true); }, function () { fertig(false); });
          } else {
            fertig(kopiereUeberFeld(roh));
          }
        });
      }

      var laden = block.querySelector('[data-fu-code-download]');
      if (laden) {
        laden.addEventListener('click', function () {
          var blob = new Blob([roh], { type: 'text/plain;charset=utf-8' });
          var a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = dateiname(bezeichnung) + '.ino';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.setTimeout(function () { URL.revokeObjectURL(a.href); }, 2000);
        });
      }

      var schalter = block.querySelector('[data-fu-code-toggle]');
      if (schalter) {
        schalter.hidden = false;
        block.setAttribute('data-offen', 'false');
        var zeilen = roh.split('\n').length;
        var zuText = schalter.querySelector('[data-fu-code-toggle-text]');
        if (zuText) {
          var muster = schalter.getAttribute('data-label-auf') || '';
          zuText.textContent = muster.replace('{zeilen}', zeilen);
        }
        schalter.addEventListener('click', function () {
          var offen = block.getAttribute('data-offen') === 'true';
          block.setAttribute('data-offen', offen ? 'false' : 'true');
          schalter.setAttribute('aria-expanded', offen ? 'false' : 'true');
          if (zuText) {
            var m2 = offen
              ? (schalter.getAttribute('data-label-auf') || '').replace('{zeilen}', zeilen)
              : schalter.getAttribute('data-label-zu') || '';
            zuText.textContent = m2;
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     Video erst nach Klick laden
     ------------------------------------------------------------------ */
  function initVideo() {
    alle('[data-fu-video]').forEach(function (fassade) {
      fassade.addEventListener('click', function (e) {
        var einbettung = fassade.getAttribute('data-fu-video-embed');
        if (!einbettung) return; /* Ohne einbettbare Adresse bleibt es ein Link. */
        e.preventDefault();
        var rahmen = document.createElement('iframe');
        rahmen.setAttribute('src', einbettung);
        rahmen.setAttribute('title', fassade.getAttribute('data-fu-video-titel') || '');
        rahmen.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
        rahmen.setAttribute('allowfullscreen', 'allowfullscreen');
        rahmen.setAttribute('loading', 'lazy');
        rahmen.className = 'fu-blog__video-rahmen';
        fassade.replaceWith(rahmen);
      });
    });
  }

  /* ------------------------------------------------------------------
     Board-Umschalter: Mikrocontroller waehlen, Varianten in den Schritten
     umschalten. Vertrag mit blocks/_fu-blog-schritte.liquid und
     snippets/fu-blog-schritt.liquid.
     ------------------------------------------------------------------ */
  /* === BEREICH BOARD === */
  function initBoards() {
    var leiste = wurzel.querySelector('[data-fu-boards]');
    if (!leiste) return;

    /* Die Leiste ist eine RADIOGRUPPE, keine Tab-Leiste — die Begruendung
       steht ausfuehrlich in blocks/_fu-blog-schritte.liquid: ein Tab
       verspricht per aria-controls ein tabpanel, und genau das gibt es hier
       nicht. Gelesen und geschrieben wird deshalb aria-checked, nicht
       aria-selected. Wer hier auf role="tab" zurueckfaellt, findet keinen
       einzigen Knopf mehr und schaltet den Umschalter still ab. */
    var knoepfe = alle('[role="radio"][data-fu-board]', leiste);
    if (knoepfe.length < 2) return;

    var gruppe = leiste.querySelector('[role="radiogroup"]') || leiste;
    var schluessel = leiste.getAttribute('data-speicher') || 'funduino-board';
    var varianten = alle('[data-fu-variante]');
    var namen = alle('[data-fu-boardname]');
    var fallbacks = alle('[data-fu-variante-fallback]');

    function handleVon(knopf) {
      return knopf.getAttribute('data-fu-board') || '';
    }

    function knopfZu(handle) {
      for (var i = 0; i < knoepfe.length; i += 1) {
        if (handleVon(knoepfe[i]) === handle) return knoepfe[i];
      }
      return null;
    }

    /* Der Boardname kommt aus der Aufschrift des Knopfes — dort steht er
       ohnehin, und ein zweites Attribut dafuer koennte vom Text abweichen. */
    function zeige(handle, fokus) {
      var knopf = knopfZu(handle);
      if (!knopf) return;

      knoepfe.forEach(function (k) {
        var ist = k === knopf;
        k.setAttribute('aria-checked', ist ? 'true' : 'false');
        k.setAttribute('tabindex', ist ? '0' : '-1');
      });
      if (fokus) knopf.focus();

      varianten.forEach(function (v) {
        v.hidden = v.getAttribute('data-fu-variante') !== handle;
      });

      var name = knopf.textContent.trim();
      if (name) {
        namen.forEach(function (s) { s.textContent = name; });
      }

      /* Der allgemeine Text eines Schritts weicht, sobald die sichtbare
         Variante einen eigenen mitbringt. Gesucht wird nur innerhalb des
         eigenen Schritts — ein Varianten-Text in Schritt 3 darf den
         allgemeinen Text von Schritt 2 nicht verdraengen. */
      fallbacks.forEach(function (fb) {
        var schritt = fb.closest('[data-fu-step]');
        var eigener = schritt
          ? schritt.querySelector('[data-fu-variante="' + handle + '"] [data-fu-variante-text]')
          : null;
        fb.hidden = !!eigener;
      });
    }

    function merke(handle) {
      try {
        window.localStorage.setItem(schluessel, handle);
      } catch (e) {
        /* Privater Modus oder volles Kontingent: die Auswahl gilt trotzdem,
           nur nicht ueber den Seitenwechsel hinaus. */
      }
    }

    function waehle(handle, fokus) {
      zeige(handle, fokus);
      merke(handle);
    }

    knoepfe.forEach(function (knopf) {
      knopf.addEventListener('click', function () { waehle(handleVon(knopf), false); });
    });

    /* Tastatur nach dem Muster der Radiogruppe: Pfeile wandern UND waehlen,
       Home und End springen an den Rand. Der Fokus folgt der Auswahl, damit
       der Screenreader das neue Board vorliest. Bei einer Radiogruppe ist
       das nicht nur erlaubt, sondern die erwartete Bedienung — anders als
       bei Tabs gibt es kein Panel, in das man erst noch hineintabben
       muesste. */
    gruppe.addEventListener('keydown', function (e) {
      var aktiv = 0;
      for (var i = 0; i < knoepfe.length; i += 1) {
        if (knoepfe[i].getAttribute('aria-checked') === 'true') { aktiv = i; break; }
      }
      var ziel = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') ziel = (aktiv + 1) % knoepfe.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') ziel = (aktiv - 1 + knoepfe.length) % knoepfe.length;
      else if (e.key === 'Home') ziel = 0;
      else if (e.key === 'End') ziel = knoepfe.length - 1;
      if (ziel === null) return;
      e.preventDefault();
      waehle(handleVon(knoepfe[ziel]), true);
    });

    /* Start: gespeichertes Board, sonst das im Markup vorausgewaehlte. Ein
       gespeicherter Handle, den diese Anleitung nicht kennt, bleibt ohne
       Wirkung — die Auswahl wird dabei NICHT ueberschrieben, damit sie in
       der naechsten Anleitung mit diesem Board wieder greift. */
    var start = null;
    try {
      start = window.localStorage.getItem(schluessel);
    } catch (e) {
      start = null;
    }
    if (!start || !knopfZu(start)) {
      var vorgewaehlt = knoepfe.filter(function (k) { return k.getAttribute('aria-checked') === 'true'; })[0] || knoepfe[0];
      start = handleVon(vorgewaehlt);
    }
    zeige(start, false);
  }
  /* === ENDE BOARD === */

  /* ------------------------------------------------------------------
     Bewertung am Ende der Anleitung. Vertrag mit blocks/_fu-blog-abschluss.liquid.
     ------------------------------------------------------------------ */
  /* === BEREICH BEWERTUNG === */
  function initBewertung() {
    var handle = wurzel.getAttribute('data-fu-blog-handle') || '';
    alle('[data-fu-rating]').forEach(function (kasten) {
      var knoepfe = alle('button[data-fu-rate]', kasten);
      if (!knoepfe.length) return;
      var meldung = kasten.querySelector('[data-fu-rating-status]');
      var schluessel = 'fu-anleitung-bewertung-' + handle;

      /* localStorage kann fehlen oder verboten sein (privates Fenster,
         Safari mit blockierten Website-Daten) — dann wirft schon der
         Zugriff. Die Bewertung funktioniert trotzdem, nur ohne Gedaechtnis. */
      function lies() {
        try { return window.localStorage.getItem(schluessel); } catch (e) { return null; }
      }
      function merke(wert) {
        try { window.localStorage.setItem(schluessel, wert); } catch (e) {}
      }

      /* Beide Knoepfe bleiben stehen, der gewaehlte ist gedrueckt. Die
         Dankesmeldung erscheint auch beim Wiederbesuch — sonst stuende ein
         gefuellter Knopf da, ohne dass klar ist, warum. */
      function zeichne(wert) {
        knoepfe.forEach(function (k) {
          k.setAttribute('aria-pressed', k.getAttribute('data-fu-rate') === wert ? 'true' : 'false');
        });
        if (meldung) {
          meldung.textContent = meldung.getAttribute('data-text-danke') || '';
          meldung.hidden = false;
        }
      }

      /* Es gibt keinen Server, der zaehlt. Die Wahl geht als Ereignis an
         Shopify Analytics und den dataLayer, wo die Auswertung stattfindet —
         beides nur, wenn es die Objekte auf der Seite ueberhaupt gibt.
         Wer zuhoert, entscheidet der Shop: ein benutzerdefiniertes Pixel
         unter Einstellungen, Kundenereignisse. Ohne Pixel verpufft der Ruf
         folgenlos, das Theme merkt davon nichts.

         Der Name traegt das Praefix "funduino:", so wie Shopify es fuer
         eigene Ereignisse vorschreibt (shopify.dev, Web Pixels API,
         "Emitting Data"): "custom events should be prefixed with the app
         name or a unique identifier". Der Doppelpunkt trennt den Absender
         vom Ereignis und haelt eigene Namen aus dem Weg der Standard-
         ereignisse, die Shopify selbst vergibt und laufend ergaenzt.
         Vorher hiess das Ereignis fu_anleitung_bewertung; wer ein Pixel
         darauf abonniert hat, muss den Namen dort mitaendern. */
      var EREIGNIS = 'funduino:anleitung_bewertet';

      function melde(wert) {
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            window.Shopify.analytics.publish(EREIGNIS, { handle: handle, wertung: wert });
          }
        } catch (e) {}
        /* Der dataLayer nimmt den Doppelpunkt zwar klaglos, aber Google Tag
           Manager tut sich mit ihm in Ausloeserbedingungen schwer. Dort
           bleibt der Unterstrich-Name. */
        try {
          if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: 'funduino_anleitung_bewertet', handle: handle, wertung: wert });
          }
        } catch (e) {}
      }

      var gespeichert = lies();
      if (gespeichert === 'gut' || gespeichert === 'schlecht') zeichne(gespeichert);

      knoepfe.forEach(function (knopf) {
        knopf.addEventListener('click', function () {
          var wert = knopf.getAttribute('data-fu-rate');
          if (wert !== 'gut' && wert !== 'schlecht') return;
          /* Derselbe Knopf noch einmal: nichts zu tun, kein zweites Ereignis. */
          if (knopf.getAttribute('aria-pressed') === 'true') return;
          zeichne(wert);
          merke(wert);
          melde(wert);
        });
      });
    });
  }
  /* === ENDE BEWERTUNG === */

  /* ------------------------------------------------------------------
     Kommentare: Antworten-Knopf fuellt das Formular vor. Vertrag mit
     sections/fu-blog-kommentare.liquid.
     ------------------------------------------------------------------ */
  /* === BEREICH KOMMENTARE === */
  function initKommentare() {
    /* Die Kommentar-Section liegt AUSSERHALB der Anleitungs-Wurzel, deshalb
       document und nicht wurzel. */
    var kasten = document.querySelector('[data-fu-kommentare]');
    if (!kasten) return;
    /* Eigener Riegel am Element: start() laeuft bei jedem
       shopify:section:load erneut, und ohne diesen Riegel bekaeme jeder
       Antworten-Knopf bei jedem Durchlauf einen weiteren click-Listener. */
    if (kasten.dataset.fuKommentareReady === 'true') return;
    kasten.dataset.fuKommentareReady = 'true';
    var knoepfe = alle('button[data-fu-reply]', kasten);
    if (!knoepfe.length) return;
    var feld = kasten.querySelector('textarea[data-fu-comment-body]');

    /* Die Knoepfe kommen versteckt aus dem Liquid und erscheinen erst, wenn
       es ein Textfeld gibt, in das sie schreiben koennten. Nach dem Absenden
       steht dort die Erfolgsmeldung statt des Formulars — dann bleiben sie
       weg. Ein Knopf, der nichts tut, ist schlimmer als keiner. */
    if (!feld) return;
    knoepfe.forEach(function (knopf) {
      knopf.hidden = false;
      knopf.addEventListener('click', function () {
        var name = (knopf.getAttribute('data-name') || '').trim();
        /* Nur ein leeres Feld wird vorbelegt. Wer schon tippt, will den
           Text nicht ueberschrieben haben — dann nur fokussieren. */
        if (name && feld.value.trim() === '') feld.value = '@' + name + ' ';
        feld.focus();
        try {
          var ende = feld.value.length;
          feld.setSelectionRange(ende, ende);
        } catch (e) {}
      });
    });
  }
  /* === ENDE KOMMENTARE === */

  /* ------------------------------------------------------------------
     Alle Teile in den Warenkorb. Vertrag mit blocks/_fu-blog-bauteile.liquid.
     ------------------------------------------------------------------ */
  /* === BEREICH ALLE TEILE === */
  /* Der Ablauf folgt Horizons product-form.js (#processBatchAddToCart), das
     fuer mehrere Zeilen auf einmal gebaut ist — es passt hier ohne Abweichung:

     1. CartLinesUpdateEvent SOFORT ausloesen, mit einem noch offenen Promise.
        Warenkorb-Symbol und -Schublade hoeren auf document; die Schublade
        oeffnet sich damit schon waehrend der Anfrage (auto-open), das Symbol
        wartet auf das Promise.
     2. POST an /cart/add.js mit {items, sections}. sections sind die IDs
        aller cart-items-component auf der Seite; Shopify liefert deren
        frisches HTML in der Antwort mit.
     3. Danach den Warenkorb als JSON holen (dieselbe Adresse, die die
        cart-items-component selbst benutzt) und das Promise mit
        {cart, detail} aufloesen. Das Symbol liest cart.totalQuantity, die
        Schublade zeichnet sich aus detail.sections neu.

     Ein Fehler von Shopify (status in der Antwort, z. B. ausverkauft) geht
     wie in product-form.js als CartErrorEvent hinaus, und zwar sofort, sobald
     er erkannt ist: assets/fu-cart-error.js hoert auf document darauf und
     zeigt Shopifys Begruendung an. Ohne das Ereignis bliebe nur die pauschale
     Meldung am Knopf stehen, der Grund („Nur noch 2 auf Lager") ginge
     verloren. Der Warenkorb wird trotzdem nachgeladen und das Promise mit
     didError aufgeloest, damit das Symbol den echten Stand zeigt, statt auf
     eine Antwort zu warten.

     Theme ist eine globale const aus snippets/scripts.liquid (KEIN
     window.Theme) — von einem Modul aus ueber typeof erreichbar. */
  function initAlleTeile() {
    var knopf = wurzel.querySelector('[data-fu-cart-all]');
    if (!knopf) return;
    if (typeof Theme === 'undefined' || !Theme.routes || !Theme.routes.cart_add_url) return;

    var items = [];
    try {
      items = JSON.parse(knopf.getAttribute('data-items') || '[]');
    } catch (e) {
      items = [];
    }
    if (!Array.isArray(items) || !items.length) return;

    var meldung = wurzel.querySelector('[data-fu-cart-all-status]');
    var meldungText = meldung ? meldung.querySelector('[data-fu-cart-all-text]') || meldung : null;

    function melde(zustand, text) {
      if (!meldung) return;
      meldungText.textContent = text;
      meldung.setAttribute('data-zustand', zustand);
      meldung.hidden = false;
    }

    function ladeWarenkorb() {
      return fetch(Theme.routes.cart_url + '.json', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then(function (antwort) {
        if (!antwort.ok) throw new Error('Warenkorb nicht geladen: ' + antwort.status);
        return antwort.json();
      });
    }

    function sektionsIds() {
      var ids = [];
      Array.prototype.forEach.call(document.querySelectorAll('cart-items-component'), function (el) {
        var id = el.getAttribute('data-section-id');
        if (id) ids.push(id);
      });
      return ids;
    }

    knopf.addEventListener('click', function () {
      if (knopf.disabled) return;
      knopf.disabled = true;
      if (meldung) meldung.hidden = true;

      var anzahl = items.reduce(function (s, i) { return s + (Number(i.quantity) || 0); }, 0);
      var zusage = CartLinesUpdateEvent.createPromise();

      /* Das Event steigt auf (bubbles), die Hoerer sitzen auf document. */
      knopf.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map(function (i) {
            return { merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 };
          }),
          promise: zusage.promise
        })
      );

      function loese(korb, antwort, fehler) {
        zusage.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
          detail: {
            items: korb.items,
            source: 'fu-blog-bauteile',
            sourceId: 'fu-cart-all',
            itemCount: anzahl,
            sections: antwort && antwort.sections,
            didError: fehler
          }
        });
      }

      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: sektionsIds().join(',') })
      })
        .then(function (r) { return r.json(); })
        .then(function (antwort) {
          var schief = !!antwort.status;
          /* Der Grund geht sofort hinaus, nicht erst nach dem Nachladen des
             Warenkorbs — scheitert auch das, greift der Reject-Zweig unten
             und der Kunde saehe nur die pauschale Meldung. Keine Beschriftung
             im Code: Angezeigt wird detail.description von Shopify; die
             englische Huelle in error steht in den SENTINELS von
             assets/fu-cart-error.js und wird dort gegen den deutschen
             Rueckfalltext getauscht. */
          if (schief) {
            knopf.dispatchEvent(
              new CartErrorEvent({
                error: antwort.message || 'Add to cart failed',
                code: 'INVALID',
                detail: {
                  description: antwort.description,
                  errors: antwort.errors
                }
              })
            );
          }
          return ladeWarenkorb().then(
            function (korb) {
              loese(korb, schief ? null : antwort, schief);
              if (schief) throw new Error(antwort.message || antwort.description || 'add failed');
            },
            function (e) {
              zusage.reject(e);
              throw e;
            }
          );
        })
        .then(function () {
          /* Erfolg: Der Knopf weicht der Meldung. Ein zweiter Klick wuerde
             die Teile doppelt einlegen — genau das soll nicht passieren. */
          knopf.hidden = true;
          melde('fertig', knopf.getAttribute('data-label-fertig') || '');
        })
        .catch(function (e) {
          /* Netzfehler: Das Promise ist noch offen und wird verworfen; die
             Hoerer im Theme fangen das ab. Nach einem Shopify-Fehler ist es
             schon aufgeloest, und reject verhallt still. */
          zusage.reject(e);
          knopf.disabled = false;
          melde('fehler', knopf.getAttribute('data-label-fehler') || '');
        });
    });
  }
  /* === ENDE ALLE TEILE === */

  function start() {
    /* Der Anleitungsteil haengt an der Wurzel und wird je Wurzel einmal
       eingerichtet. */
    var w = document.querySelector('[data-fu-blog]');
    if (w && w.dataset.fuBlogReady !== 'true') {
      w.dataset.fuBlogReady = 'true';
      wurzel = w;
      initGalerien();
      initFortschritt();
      initSchrittverfolgung();
      initListeHalt();
      initMehrLesen();
      initTeilen();
      initCode();
      initVideo();
      initBoards();
      initBewertung();
      initAlleTeile();
    }

    /* initKommentare steht BEWUSST ausserhalb des Riegels. Die Kommentar-
       Section liegt ausserhalb der Anleitungs-Wurzel (siehe Kommentar dort)
       und wird im Theme-Editor eigenstaendig neu gerendert. Laege der Aufruf
       innerhalb, braeche start() bei unveraenderter Wurzel vorher ab, und die
       Antworten-Knoepfe der frisch geladenen Section blieben dauerhaft
       versteckt — sie kommen mit hidden aus dem Liquid und werden erst hier
       sichtbar gemacht. */
    initKommentare();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* Theme-Editor: Shopify rendert einzelne Sections neu, ohne die Seite zu
     laden. Ohne diese Zeile bleibt alles tot, was danach im DOM steht. */
  document.addEventListener('shopify:section:load', start);
})();
